import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  Phone, 
  ArrowRight, 
  ArrowDown,
  Plus, 
  Minus, 
  Quote, 
  CheckCircle2, 
  Info, 
  AlertCircle, 
  ShieldCheck, 
  EyeOff, 
  Sliders, 
  Sparkles, 
  MapPin, 
  Laptop, 
  HelpCircle,
  Coins,
  FileText,
  ArrowUp,
  Brain,
  Clock,
  Activity,
  Award,
  GraduationCap,
  UserCheck,
  Check
} from 'lucide-react';

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="bg-white border border-[#E8DDCE] rounded-2xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-[#FAF7F2] transition-colors group cursor-pointer"
        aria-expanded={isOpen}
      >
        <span className="font-bold text-[#123F13] md:text-lg pr-8">{question}</span>
        <div className={`shrink-0 text-[#123F13] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-[#3B352F]/70 leading-relaxed border-t border-[#FAF7F2] pt-4">
              <div className="space-y-4">
                {answer}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Eksamensangst = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const canonicalUrl = "https://corehypnose.dk/eksamensangst-praestationsangst";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Er det normalt at være nervøs før en eksamen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja. Nervøsitet før noget vigtigt er almindeligt. Det bliver først et større problem, når bekymring, kontrol, søvnproblemer, undgåelse eller efterfølgende grubleri fylder så meget, at det begrænser dig."
        }
      },
      {
        "@type": "Question",
        "name": "Hvorfor går jeg blank, når jeg egentlig kan stoffet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Der kan være flere grunde, og én forklaring passer ikke på alle. For nogle bliver en stor del af opmærksomheden optaget af selvmonitorering, kontrol og frygten for at gå blank. Det betyder ikke nødvendigvis, at stoffet er væk."
        }
      },
      {
        "@type": "Question",
        "name": "Skal jeg prøve at fjerne nervøsiteten?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ikke nødvendigvis. Målet er ikke, at du skal være helt rolig, før du kan præstere. Du kan være nervøs og stadig svare, tænke og handle."
        }
      },
      {
        "@type": "Question",
        "name": "Hvorfor kan jeg ikke stoppe med at forberede mig?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Forberedelse er vigtig. Men den kan glide over i et forsøg på at skabe fuldstændig sikkerhed. Den midlertidige ro kan hurtigt blive afløst af ny tvivl."
        }
      },
      {
        "@type": "Question",
        "name": "Hvad hvis jeg bliver rød, ryster eller får høj puls?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kroppen må gerne reagere. Høj puls, rysten eller varme i ansigtet er ikke det samme som, at du ikke kan gennemføre."
        }
      },
      {
        "@type": "Question",
        "name": "Kan jeg lære at holde fokus under eksamen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Opmærksomhed er ikke noget, du skal holde perfekt. Men den kan trænes og bruges mere fleksibelt."
        }
      },
      {
        "@type": "Question",
        "name": "Kan metakognitiv terapi hjælpe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Metakognitiv terapi retter blandt andet fokus mod bekymring, grubleri, opmærksomhed og de strategier, du bruger, når angsten kommer. Behandling er individuel, og jeg kan ikke love et bestemt resultat."
        }
      },
      {
        "@type": "Question",
        "name": "Bruger du hypnose?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, når det giver mening for dig. Hypnose kan bruges som rolig guidning og fokuseret opmærksomhed. Hypnose er valgfrit, og du kan helt fravælge det."
        }
      },
      {
        "@type": "Question",
        "name": "Kan jeg starte med én session?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja. Den første behandlingssession koster 375 kr., varer 50 minutter og er kun for nye klienter. Du behøver ikke binde dig til et forløb."
        }
      },
      {
        "@type": "Question",
        "name": "Kan behandlingen foregå online?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja. Du kan vælge session i klinikken på Valby Tingsted 7 eller online hjemmefra."
        }
      },
      {
        "@type": "Question",
        "name": "Hvad hvis jeg også har ADHD eller autisme?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Det er vigtigt at se på din samlede situation og ikke reducere alt til én forklaring. Jeg kan tage højde for blandt andet opmærksomhed, belastning, struktur, sanser og behov for tydelige rammer."
        }
      },
      {
        "@type": "Question",
        "name": "Hvad koster en almindelig session som studerende?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Som studerende får du 25 % rabat på almindelige individuelle sessioner. Prisen er 1.350 kr. for 50 minutter. Den første behandlingssession for nye klienter koster 375 kr."
        }
      }
    ]
  };

  const faqData = [
    {
      question: "Er det normalt at være nervøs før en eksamen?",
      answer: (
        <div className="space-y-4">
          <p>
            Ja. Nervøsitet før noget vigtigt er almindeligt. Det bliver først et større problem, når bekymring, kontrol, søvnproblemer, undgåelse eller efterfølgende grubleri fylder så meget, at det begrænser dig.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <p className="text-xs text-[#3B352F]/80 font-semibold">Nervøsitet kan være til stede</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <p className="text-xs text-[#3B352F]/80 font-semibold">Du kan stadig handle</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <p className="text-xs text-[#3B352F]/80 font-semibold">Problemet er, når angsten bliver styrende</p>
            </div>
          </div>
        </div>
      )
    },
    {
      question: "Hvorfor går jeg blank, når jeg egentlig kan stoffet?",
      answer: (
        <div className="space-y-4">
          <p>
            Der kan være flere grunde, og én forklaring passer ikke på alle. For nogle bliver en stor del af opmærksomheden optaget af selvmonitorering, kontrol og frygten for at gå blank.
          </p>
          <p>
            Det betyder ikke nødvendigvis, at stoffet er væk. Det kan være sværere at få adgang til det, du ved, når meget mental kapacitet samtidig bruges på at overvåge dig selv.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <p className="text-xs text-[#3B352F]/80 font-semibold">Opgaven</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <p className="text-xs text-[#3B352F]/80 font-semibold">Overvågning af mig selv</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <p className="text-xs text-[#3B352F]/80 font-semibold">Kontrol af nervøsiteten</p>
            </div>
          </div>
        </div>
      )
    },
    {
      question: "Skal jeg prøve at fjerne nervøsiteten?",
      answer: (
        <div className="space-y-4">
          <p>
            Ikke nødvendigvis. Målet er ikke, at du skal være helt rolig, før du kan præstere. Du kan være nervøs og stadig svare, tænke og handle.
          </p>
          <p>
            Jeg kan i stedet hjælpe dig med at arbejde med, at nervøsiteten får mindre magt over din opmærksomhed og det, du gør.
          </p>
        </div>
      )
    },
    {
      question: "Hvorfor kan jeg ikke stoppe med at forberede mig?",
      answer: (
        <div className="space-y-4">
          <p>
            Forberedelse er vigtig. Men den kan glide over i et forsøg på at skabe fuldstændig sikkerhed: “Jeg læser lige én gang til” eller “Jeg stopper først, når jeg føler mig helt klar”.
          </p>
          <p>
            Den midlertidige ro kan hurtigt blive afløst af ny tvivl. Så begynder cirklen igen.
          </p>
        </div>
      )
    },
    {
      question: "Hvad hvis jeg bliver rød, ryster eller får høj puls?",
      answer: (
        <div className="space-y-4">
          <p>
            Kroppen må gerne reagere. Høj puls, rysten eller varme i ansigtet er ikke det samme som, at du ikke kan gennemføre.
          </p>
          <p>
            I behandlingen kan jeg hjælpe dig med at arbejde med mindre overvågning og mindre kamp mod reaktionerne, så de ikke får hele din opmærksomhed.
          </p>
          <div className="pt-2">
            <Link to="/social-angst" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
              Læs også om social angst <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan jeg lære at holde fokus under eksamen?",
      answer: (
        <div className="space-y-4">
          <p>
            Opmærksomhed er ikke noget, du skal holde perfekt. Men den kan trænes og bruges mere fleksibelt.
          </p>
          <p>
            Det kan handle om at opdage, når du bliver fanget i selvmonitorering, og øve dig i at vende tilbage til spørgsmålet eller den konkrete opgave uden først at skulle føle dig helt rolig.
          </p>
        </div>
      )
    },
    {
      question: "Kan metakognitiv terapi hjælpe?",
      answer: (
        <div className="space-y-4">
          <p>
            Metakognitiv terapi retter blandt andet fokus mod bekymring, grubleri, opmærksomhed og de strategier, du bruger, når angsten kommer.
          </p>
          <p>
            Jeg ser sammen med dig på, hvad der holder problemet i gang før, under og efter præstationen. Behandling er individuel, og jeg kan ikke love et bestemt resultat.
          </p>
          <div className="pt-2">
            <Link to="/metakognitiv-terapi" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
              Læs om metakognitiv terapi <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Bruger du hypnose?",
      answer: (
        <div className="space-y-4">
          <p>
            Ja, når det giver mening for dig. Hypnose kan bruges som rolig guidning og fokuseret opmærksomhed, for eksempel til mental forberedelse og til at øve en anden måde at møde uro på.
          </p>
          <p>
            Hypnose er valgfrit, og du kan helt fravælge det.
          </p>
          <div className="pt-2">
            <Link to="/hypnose" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
              Læs om hypnose <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan jeg starte med én session?",
      answer: (
        <div className="space-y-4">
          <p>
            Ja. Den første behandlingssession koster 375 kr., varer 50 minutter og er kun for nye klienter. Du behøver ikke binde dig til et forløb.
          </p>
          <div className="flex flex-wrap gap-4 pt-2">
            <Link to="/bestil-tid" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
              Book første behandlingssession <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan behandlingen foregå online?",
      answer: (
        <div className="space-y-4">
          <p>
            Ja. Du kan vælge session i klinikken på Valby Tingsted 7 eller online hjemmefra.
          </p>
          <p>
            Online kan være særligt praktisk, hvis transport tager meget energi, eller du bor langt væk.
          </p>
        </div>
      )
    },
    {
      question: "Hvad hvis jeg også har ADHD eller autisme?",
      answer: (
        <div className="space-y-4">
          <p>
            Det er vigtigt at se på din samlede situation og ikke reducere alt til én forklaring. Jeg kan tage højde for blandt andet opmærksomhed, belastning, struktur, sanser og behov for tydelige rammer.
          </p>
          <p>
            Du behøver ikke passe ind i en standardform.
          </p>
          <div className="flex flex-wrap gap-4 pt-2">
            <Link to="/adhd" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
              Læs om ADHD <ArrowRight size={14} />
            </Link>
            <Link to="/autismevenlig-terapi" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
              Læs om autismevenlig session <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvad koster en almindelig session som studerende?",
      answer: (
        <div className="space-y-4">
          <p>
            Som studerende får du 25 % rabat på almindelige individuelle sessioner. Prisen er 1.350 kr. for 50 minutter.
          </p>
          <p>
            Den første behandlingssession for nye klienter koster 375 kr.
          </p>
          <div className="pt-2">
            <Link to="/priser" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
              Se hele prisoversigten <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="bg-[#FFFCF8] text-[#3B352F] font-sans antialiased">
      <Helmet>
        <title>Eksamensangst og præstationsangst | Behandling i Valby og online</title>
        <meta name="description" content="Få hjælp til eksamensangst og præstationsangst. Arbejd med bekymring, selvfokus og frygten for at gå blank med metakognitiv terapi og valgfri hypnose i Valby eller online." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Eksamensangst og præstationsangst | Core Hypnose" />
        <meta property="og:description" content="Arbejd med bekymring, selvfokus og frygten for at gå blank. Metakognitiv terapi og valgfri hypnose i Valby eller online." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-br from-[#FFFCF8] via-[#FAF7F2] to-[#F4EFE7] border-b border-[#E8DDCE]/50">
        <div className="absolute top-0 right-0 w-96 h-96 bg-radial from-[#8C2F12]/5 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-radial from-[#123F13]/5 to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-block text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase bg-[#8C2F12]/10 px-3 py-1 rounded-full">
                Eksamensangst og præstationsangst
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-bold text-[#123F13] leading-tight">
                Når presset fylder mere end opgaven
              </h1>
              <p className="text-lg sm:text-xl text-[#3B352F]/80 leading-relaxed max-w-2xl">
                Du kan være godt forberedt og stadig blive fanget i bekymring, selvfokus og frygten for at gå blank. Jeg hjælper dig med at arbejde med det mønster, der gør præstationen sværere.
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/bestil-tid" className="inline-flex items-center justify-center bg-[#193f10] text-white px-6 py-3.5 rounded-lg font-bold hover:bg-[#193f10]/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 duration-200">
                  Book første behandlingssession
                </Link>
                <a href="#moensteret" className="inline-flex items-center justify-center border-2 border-[#193f10] text-[#193f10] px-6 py-3.5 rounded-lg font-bold hover:bg-[#faf7f2] transition-all duration-200">
                  Se hvad der kan holde angsten i gang
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl border border-[#E8DDCE] shadow-xl space-y-6 relative">
                <Quote className="absolute top-4 right-4 text-[#8C2F12]/10 w-16 h-16 pointer-events-none" />
                <h3 className="font-serif font-bold text-xl text-[#123F13] border-b border-[#E8DDCE]/50 pb-3">
                  Måske kender du en eller flere af disse tanker
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-[#FAF7F2] rounded-2xl border-l-4 border-[#8C2F12] text-[#3B352F]/80 italic">
                    “Hvad hvis jeg går blank?”
                  </div>
                  <div className="p-4 bg-[#FAF7F2] rounded-2xl border-l-4 border-[#8C2F12] text-[#3B352F]/80 italic">
                    “Jeg kan stoffet derhjemme, men når det gælder, forsvinder det.”
                  </div>
                  <div className="p-4 bg-[#FAF7F2] rounded-2xl border-l-4 border-[#8C2F12] text-[#3B352F]/80 italic">
                    “Hvad hvis de kan se, at jeg er nervøs?”
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E8DDCE] rounded-full text-sm font-bold text-[#3B352F]/80 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#123F13]" /> 375 kr. første session
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E8DDCE] rounded-full text-sm font-bold text-[#3B352F]/80 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#123F13]" /> 50 minutter
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E8DDCE] rounded-full text-sm font-bold text-[#3B352F]/80 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#123F13]" /> Valby eller online
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E8DDCE] rounded-full text-sm font-bold text-[#3B352F]/80 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#123F13]" /> Ingen binding
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Du kan stoffet */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
              Måske kender du det
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
              Du kan stoffet.<br />Alligevel vokser tvivlen.
            </h2>
            <p className="text-[#3B352F]/70 text-lg">
              For nogle begynder presset længe før selve eksamenen, mødet eller præsentationen. Det kan fylde om aftenen, i forberedelsen og i de mange forsøg på at blive helt klar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <h4 className="font-serif font-bold text-lg text-[#123F13] mb-2">Før præstationen</h4>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                Du gennemgår alt det, der kan gå galt, prøver at forudse alle spørgsmål eller mærker hele tiden efter, om du føler dig klar.
              </p>
            </div>
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <h4 className="font-serif font-bold text-lg text-[#123F13] mb-2">Når det gælder</h4>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                Du bliver meget opmærksom på stemmen, kroppen, hukommelsen og på, hvordan du mon virker på andre.
              </p>
            </div>
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <h4 className="font-serif font-bold text-lg text-[#123F13] mb-2">Bagefter</h4>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                Du gennemgår fejl, sammenligner dig og bruger lang tid på det, du kunne have sagt eller gjort anderledes.
              </p>
            </div>
            <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <h4 className="font-serif font-bold text-lg text-[#8C2F12] mb-2">Det kan føles paradoksalt</h4>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                Jo mere du forsøger at skabe sikkerhed og kontrol, desto mere plads kan tvivlen få.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Mønsteret (Før, Under, Efter) */}
      <section id="moensteret" className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
              Før, under og efter
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
              Angsten kan begynde <br />længe før selve præstationen
            </h2>
            <p className="text-[#3B352F]/70 text-lg">
              For mange er det ikke kun de minutter, hvor de skal præstere, der er svære. Det er hele det mentale arbejde omkring situationen, der kommer til at fylde.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm relative">
              <span className="inline-block text-xs font-extrabold tracking-wider text-[#8C2F12] uppercase bg-[#8C2F12]/10 px-3 py-1 rounded-full mb-4">
                Før
              </span>
              <h3 className="font-serif font-bold text-xl text-[#123F13] mb-4">Bekymring og kontrol</h3>
              <ul className="space-y-3 text-sm text-[#3B352F]/80">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>“Hvad nu hvis?”</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Mental gennemgang</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Forsøg på at forudse alt</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Kontrol af, om du føler dig klar</span>
                </li>
              </ul>

              {/* Arrow to next step (pointing right on desktop, hidden on mobile) */}
              <div className="hidden lg:flex absolute lg:-right-4 lg:top-1/2 lg:-translate-y-1/2 z-10 bg-white border border-[#E8DDCE] w-8 h-8 rounded-full items-center justify-center shadow-sm text-[#8C2F12]">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm relative">
              <span className="inline-block text-xs font-extrabold tracking-wider text-[#8C2F12] uppercase bg-[#8C2F12]/10 px-3 py-1 rounded-full mb-4">
                Under
              </span>
              <h3 className="font-serif font-bold text-xl text-[#123F13] mb-4">Selvfokus og overvågning</h3>
              <ul className="space-y-3 text-sm text-[#3B352F]/80">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Hvordan lyder min stemme?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Kan de se min nervøsitet?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Husker jeg det hele?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Hvad hvis jeg går blank?</span>
                </li>
              </ul>

              {/* Arrow to next step (pointing right on desktop, hidden on mobile) */}
              <div className="hidden lg:flex absolute lg:-right-4 lg:top-1/2 lg:-translate-y-1/2 z-10 bg-white border border-[#E8DDCE] w-8 h-8 rounded-full items-center justify-center shadow-sm text-[#8C2F12]">
                <i className="fa-solid fa-arrow-right text-xs" />
              </div>
            </div>

            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm relative">
              <span className="inline-block text-xs font-extrabold tracking-wider text-[#8C2F12] uppercase bg-[#8C2F12]/10 px-3 py-1 rounded-full mb-4">
                Efter
              </span>
              <h3 className="font-serif font-bold text-xl text-[#123F13] mb-4">Fejlfinding og grubleri</h3>
              <ul className="space-y-3 text-sm text-[#3B352F]/80">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Gennemgang af fejl</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Selvkritik</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Sammenligning</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Ny usikkerhed før næste gang</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Hvorfor kan hovedet pludselig føles tomt? */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
                Frygten for at gå blank
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
                Hvorfor kan hovedet pludselig føles tomt?
              </h2>
              <p className="text-lg text-[#3B352F]/80 leading-relaxed">
                At gå blank betyder ikke nødvendigvis, at du ikke kan stoffet.
              </p>
              <p className="text-[#3B352F]/70 leading-relaxed">
                Når en stor del af opmærksomheden samtidig bruges på at overvåge dig selv, kontrollere nervøsiteten og lede efter tegn på, at noget er ved at gå galt, kan selve opgaven få mindre plads.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 shadow-xl space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-4 bg-[#FAF7F2] rounded-2xl border border-[#E8DDCE]">
                    <span className="w-8 h-8 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold shrink-0">1</span>
                    <span className="font-bold text-[#123F13]">Opgaven</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-[#FAF7F2] rounded-2xl border border-[#E8DDCE]">
                    <span className="w-8 h-8 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold shrink-0">+</span>
                    <span className="font-bold text-[#123F13]">Overvågning af mig selv</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-[#FAF7F2] rounded-2xl border border-[#E8DDCE]">
                    <span className="w-8 h-8 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold shrink-0">+</span>
                    <span className="font-bold text-[#123F13]">Kontrol af nervøsitet</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-[#FAF7F2] rounded-2xl border border-[#E8DDCE]">
                    <span className="w-8 h-8 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold shrink-0">+</span>
                    <span className="font-bold text-[#123F13]">Frygt for at gå blank</span>
                  </div>
                </div>
                <div className="p-4 bg-[#123F13] text-white text-center font-bold rounded-2xl shadow-md">
                  Mere mental belastning
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Når forberedelse ikke længere giver mere ro */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
              Forberedelse og sikkerhed
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
              Når forberedelse ikke <br />længere giver mere ro
            </h2>
            <p className="text-[#3B352F]/70 text-lg">
              Forberedelse er vigtig. Problemet er ikke at forberede sig. Problemet kan opstå, når forberedelsen bliver et forsøg på at fjerne al usikkerhed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center text-center">
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl font-bold text-[#123F13] shadow-sm">
              Forberedelse
            </div>
            <div className="text-[#8C2F12] text-2xl font-bold md:rotate-0 rotate-90">&rarr;</div>
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl font-bold text-[#123F13] shadow-sm">
              Mere kontrol
            </div>
            <div className="text-[#8C2F12] text-2xl font-bold md:rotate-0 rotate-90">&rarr;</div>
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl font-bold text-[#123F13] shadow-sm">
              Midlertidig ro
            </div>
            <div className="text-[#8C2F12] text-2xl font-bold md:rotate-0 rotate-90 md:col-start-5 md:row-start-2">&darr;</div>
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl font-bold text-[#123F13] shadow-sm md:col-start-5 md:row-start-3">
              Ny tvivl
            </div>
            <div className="text-[#8C2F12] text-2xl font-bold md:rotate-180 rotate-90 md:col-start-3 md:row-start-3">&rarr;</div>
            <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl font-bold text-[#123F13] shadow-sm md:col-start-1 md:row-start-3">
              Mere forberedelse
            </div>
            <div className="text-[#8C2F12] text-2xl font-bold md:rotate-270 rotate-90 md:col-start-1 md:row-start-2">&rarr;</div>
          </div>

          <div className="mt-12 p-6 bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl text-[#8C2F12]">
            <p className="font-bold mb-2">En vigtig forskel:</p>
            <p className="text-sm text-[#3B352F]/80">
              Du må gerne forberede dig grundigt. Jeg undersøger sammen med dig, om du også prøver at forberede dig væk fra enhver usikkerhed — og hvad det koster dig i tid, søvn og mental energi.
            </p>
          </div>
        </div>
      </section>

      {/* Section 5: Du behøver ikke være helt rolig */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-[#123F13] to-[#193f10] text-white rounded-3xl overflow-hidden shadow-xl grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-5 p-8 sm:p-12 bg-[#193f10] flex flex-col justify-center space-y-4">
              <span className="text-xs font-extrabold tracking-widest text-white/80 uppercase">
                Et andet mål
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold leading-tight">
                Du behøver ikke være helt rolig for at kunne præstere
              </h2>
            </div>
            <div className="lg:col-span-7 p-8 sm:p-12 flex flex-col justify-center space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span aria-hidden="true" className="w-6 h-6 rounded-full bg-[#FAF7F2] text-[#123F13] flex items-center justify-center font-bold shrink-0 text-sm">✓</span>
                  <p className="text-lg">Du kan være nervøs og stadig kunne tænke.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span aria-hidden="true" className="w-6 h-6 rounded-full bg-[#FAF7F2] text-[#123F13] flex items-center justify-center font-bold shrink-0 text-sm">✓</span>
                  <p className="text-lg">Du kan mærke uro og stadig svare.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span aria-hidden="true" className="w-6 h-6 rounded-full bg-[#FAF7F2] text-[#123F13] flex items-center justify-center font-bold shrink-0 text-sm">✓</span>
                  <p className="text-lg">Du behøver ikke vente på, at kroppen føles perfekt, før du går i gang.</p>
                </div>
              </div>
              <p className="text-sm text-white/80 border-t border-white/10 pt-4">
                Målet er ikke nødvendigvis at fjerne høj puls, sommerfugle i maven eller al spænding. Det kan i stedet være at gøre nervøsiteten mindre styrende.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Når opmærksomheden vender indad */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
              Opmærksomhed
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
              Når opmærksomheden vender indad
            </h2>
            <p className="text-[#3B352F]/70 text-lg">
              Under en præstation kan opmærksomheden blive fanget i overvågning af dig selv. Det kan gøre det sværere at holde kontakten med spørgsmålet, opgaven, samtalen eller det, der faktisk sker omkring dig.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm">
              <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase mb-2 block">
                Indad
              </span>
              <h3 className="font-serif font-bold text-xl text-[#123F13] mb-4">Hvordan virker jeg?</h3>
              <ul className="space-y-3 text-sm text-[#3B352F]/80">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Ryster mine hænder?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Lyder min stemme usikker?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Kan de se, at jeg er nervøs?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                  <span>Hvor hurtigt slår mit hjerte?</span>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-2 text-center text-[#8C2F12] text-2xl font-bold lg:rotate-0 rotate-90">
              ↔
            </div>

            <div className="lg:col-span-5 bg-[#123F13] text-white rounded-2xl p-8 shadow-md">
              <span className="text-xs font-extrabold tracking-widest text-white/80 uppercase mb-2 block">
                Udad
              </span>
              <h3 className="font-serif font-bold text-xl text-white mb-4">Hvad er min opgave lige nu?</h3>
              <ul className="space-y-3 text-sm text-white/80">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FAF7F2] mt-2 shrink-0" />
                  <span>Hvad blev jeg spurgt om?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FAF7F2] mt-2 shrink-0" />
                  <span>Hvad er næste konkrete skridt?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FAF7F2] mt-2 shrink-0" />
                  <span>Hvad sker der faktisk i rummet?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FAF7F2] mt-2 shrink-0" />
                  <span>Hvad vil jeg svare på nu?</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="mt-8 text-sm text-[#3B352F]/70 max-w-3xl">
            Det handler ikke om, at du “bare skal flytte fokus”. Opmærksomhed kan trænes, så du får mere fleksibilitet i, hvor længe du bliver fanget i selvmonitorering.
          </p>
        </div>
      </section>

      {/* Section 7: Efterbearbejdning */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 shadow-xl space-y-6">
                <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase block">
                  Når det er overstået
                </span>
                <h3 className="font-serif font-bold text-xl text-[#123F13]">
                  Præstationen slutter. Hovedet fortsætter.
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-[#FAF7F2] rounded-2xl text-center font-bold text-[#123F13]">
                    Præstationen slutter
                  </div>
                  <div className="text-center text-[#8C2F12] font-bold">&darr;</div>
                  <div className="p-4 bg-[#FAF7F2] rounded-2xl text-center font-bold text-[#123F13]">
                    Mental gennemgang og fejlfinding
                  </div>
                  <div className="text-center text-[#8C2F12] font-bold">&darr;</div>
                  <div className="p-4 bg-[#FAF7F2] rounded-2xl text-center font-bold text-[#123F13]">
                    Mere usikkerhed
                  </div>
                  <div className="text-center text-[#8C2F12] font-bold">&darr;</div>
                  <div className="p-4 bg-[#FAF7F2] rounded-2xl text-center font-bold text-[#123F13]">
                    Større frygt næste gang
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
                Efterbearbejdning
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
                Når du analyserer <br />det hele igen og igen
              </h2>
              <p className="text-lg text-[#3B352F]/80 italic">
                “Hvorfor sagde jeg det?” “Jeg burde have svaret anderledes.” “De må have tænkt, at jeg var dårlig.”
              </p>
              <p className="text-[#3B352F]/70 leading-relaxed">
                Det er muligt at lære af en præstation uden at analysere den i timevis. I behandlingen undersøger jeg sammen med dig forskellen mellem en kort, nyttig evaluering og den lange mentale gennemgang, der holder tvivlen i gang.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8: Metakognitiv terapi */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
                Metakognitiv terapi
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
                Sådan arbejder jeg med eksamensangst og præstationsangst
              </h2>
              <p className="text-lg text-[#3B352F]/80">
                Jeg arbejder ikke kun med, hvad du er bange for. Jeg ser sammen med dig på, hvad der sker før, under og efter præstationen.
              </p>
              <p className="text-[#3B352F]/70 leading-relaxed">
                Jeg undersøger sammen med dig, hvor meget tid bekymringen får, hvad du prøver at kontrollere, hvor meget du overvåger dig selv, og hvad du gør, når frygten for at gå blank opstår.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-4">
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm flex gap-4">
                <span className="w-10 h-10 rounded-full bg-[#FAF7F2] text-[#8C2F12] flex items-center justify-center font-bold shrink-0">1</span>
                <div>
                  <h4 className="font-bold text-[#123F13] mb-1">Kortlæg mønsteret</h4>
                  <p className="text-sm text-[#3B352F]/70">Hvornår begynder angsten, og hvad gør du før, under og efter?</p>
                </div>
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm flex gap-4">
                <span className="w-10 h-10 rounded-full bg-[#FAF7F2] text-[#8C2F12] flex items-center justify-center font-bold shrink-0">2</span>
                <div>
                  <h4 className="font-bold text-[#123F13] mb-1">Undersøg den mentale strategi</h4>
                  <p className="text-sm text-[#3B352F]/70">Hvad sker der, når du prøver at skabe sikkerhed, kontrollere tanker eller overvåge dig selv?</p>
                </div>
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm flex gap-4">
                <span className="w-10 h-10 rounded-full bg-[#FAF7F2] text-[#8C2F12] flex items-center justify-center font-bold shrink-0">3</span>
                <div>
                  <h4 className="font-bold text-[#123F13] mb-1">Træn mere fleksibel opmærksomhed</h4>
                  <p className="text-sm text-[#3B352F]/70">Konkrete øvelser kan hjælpe dig med at få mere frihed til at vende tilbage til opgaven.</p>
                </div>
              </div>
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm flex gap-4">
                <span className="w-10 h-10 rounded-full bg-[#FAF7F2] text-[#8C2F12] flex items-center justify-center font-bold shrink-0">4</span>
                <div>
                  <h4 className="font-bold text-[#123F13] mb-1">Øv en anden måde at møde nervøsiteten på</h4>
                  <p className="text-sm text-[#3B352F]/70">Mindre kamp, mindre overvågning og et mere realistisk næste skridt.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9: Hypnose */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-[#FFFCF8] to-[#FAF7F2] border border-[#E8DDCE] rounded-3xl p-8 sm:p-12 shadow-xl relative overflow-hidden">
            <div className="absolute -right-20 -bottom-20 w-64 h-64 rounded-full border-[28px] border-[#8C2F12]/10 pointer-events-none" />
            
            <span className="inline-block text-xs font-extrabold tracking-wider text-white bg-[#123F13] px-3 py-1 rounded-full mb-6">
              Valgfrit
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13] mb-4">
              Hypnose kan bruges som støtte — hvis det giver mening for dig
            </h2>
            <p className="text-lg text-[#3B352F]/80 max-w-3xl mb-8">
              Hypnose er rolig guidning og fokuseret opmærksomhed. Det er ikke mystik, og du mister ikke kontrollen.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
              <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl shadow-sm">
                <h4 className="font-bold text-[#123F13] mb-2">Mental forberedelse</h4>
                <p className="text-sm text-[#3B352F]/70">At forestille sig situationen og øve en anden måde at møde den på.</p>
              </div>
              <div className="bg-white border border-[#E8DDCE] p-6 rounded-2xl shadow-sm">
                <h4 className="font-bold text-[#123F13] mb-2">Mindre kamp</h4>
                <p className="text-sm text-[#3B352F]/70">At øve sig i ikke at gøre kroppens reaktioner til et tegn på, at noget er galt.</p>
              </div>
            </div>

            <p className="mt-8 text-sm text-[#3B352F]/70 border-t border-[#E8DDCE]/50 pt-4">
              <strong>Hypnose er aldrig et krav.</strong> Det giver ingen garanti for et bestemt resultat og kan helt fravælges.
            </p>
          </div>
        </div>
      </section>

      {/* Section 10: Pris og forløb */}
      <section id="foerste-session" className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-7 bg-[#123F13] text-white rounded-3xl p-8 sm:p-12 shadow-xl flex flex-col justify-between">
              <div>
                <span className="text-xs font-extrabold tracking-widest text-[#FAF7F2]/80 uppercase block mb-2">
                  Din første behandlingssession
                </span>
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-6">
                  Start med at skabe overblik
                </h2>
                <div className="text-5xl font-serif font-bold text-white mb-2">
                  375 kr.
                </div>
                <p className="text-white/70 text-sm mb-8">
                  50 minutter · kun for nye klienter · Valby eller online
                </p>

                <div className="space-y-4 border-t border-white/10 pt-6">
                  <div className="flex items-start gap-3">
                    <span className="text-[#FAF7F2] font-bold shrink-0">✓</span>
                    <p className="text-sm text-white/80">Personlig kortlægning af, hvad der sker før, under og efter</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#FAF7F2] font-bold shrink-0">✓</span>
                    <p className="text-sm text-white/80">Konkrete metakognitive spørgsmål og praktiske øvelser</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#FAF7F2] font-bold shrink-0">✓</span>
                    <p className="text-sm text-white/80">Arbejde med opmærksomhed og mental kontrol</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#FAF7F2] font-bold shrink-0">✓</span>
                    <p className="text-sm text-white/80">Tydelig afklaring af et realistisk næste skridt</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#FAF7F2] font-bold shrink-0">✓</span>
                    <p className="text-sm text-white/80">Personlig behandlingsplan</p>
                  </div>
                </div>
              </div>

              <div className="pt-8">
                <Link to="/bestil-tid" className="inline-flex items-center justify-center bg-white text-[#123F13] px-6 py-3.5 rounded-full font-bold hover:bg-[#FAF7F2] transition-all shadow-md">
                  Book første behandlingssession
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white border border-[#E8DDCE] rounded-3xl p-8 sm:p-10 shadow-sm flex flex-col justify-between">
              <div className="space-y-6">
                <h3 className="font-serif font-bold text-2xl text-[#123F13]">
                  Du behøver ikke binde dig til et forløb
                </h3>
                <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                  Den første session står for sig selv. Jeg bruger den sammen med dig til at forstå dit mønster og finde ud af, hvad der giver mening som næste skridt.
                </p>

                <ul className="space-y-3 text-sm text-[#3B352F]/80">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Betaling sker altid inden sessionen starter.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Er du studerende, får du 25 % rabat på almindelige individuelle sessioner.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Ved lægehenvisning gives 300 kr. rabat på en almindelig individuel session.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] mt-2 shrink-0" />
                    <span>Se fuldt prisoverblik for almindelige sessioner og klippekort.</span>
                  </li>
                </ul>

                <p className="pt-2">
                  <Link to="/priser" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
                    Se alle priser og muligheder <ArrowRight size={14} />
                  </Link>
                </p>
              </div>

              <div className="mt-8 p-4 bg-[#FAF7F2] rounded-2xl text-xs text-[#3B352F]/80 leading-relaxed">
                <strong>Afbud og ændringer:</strong> Det skal ske senest 24 timer før sessionen. Ved senere afbud koster det ét klip fra klippekortet eller fuld pris for sessionen. Reglen gælder også ved akut sygdom. Kan du ikke møde fysisk, kan den planlagte session gennemføres online i stedet.
              </div>
            </div>
          </div>

          <div className="mt-8 bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <strong className="text-[#8C2F12] text-lg block mb-1">Særlig pris for studerende</strong>
              <span className="text-sm text-[#3B352F]/70">Er du studerende, får du 25 % rabat på almindelige individuelle sessioner på 50 minutter.</span>
            </div>
            <div className="text-3xl font-serif font-bold text-[#123F13] shrink-0">
              1.350 kr.
            </div>
          </div>
        </div>
      </section>

      {/* Section 11: Session i Valby eller online */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
              Vælg det, der passer dig
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
              Session i Valby eller online
            </h2>
            <p className="text-[#3B352F]/70 text-lg">
              Ingen af mulighederne er generelt bedst. Vælg den ramme, der gør det mest realistisk for dig at deltage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm">
              <span className="inline-block text-xs font-extrabold tracking-wider text-[#8C2F12] uppercase bg-[#8C2F12]/10 px-3 py-1 rounded-full mb-4">
                I klinikken
              </span>
              <h3 className="font-serif font-bold text-2xl text-[#123F13] mb-2">Valby</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Du kan møde mig på Valby Tingsted 7, 2500 Valby.
              </p>
            </div>

            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm flex flex-col justify-between">
              <div>
                <span className="inline-block text-xs font-extrabold tracking-wider text-[#8C2F12] uppercase bg-[#8C2F12]/10 px-3 py-1 rounded-full mb-4">
                  Hjemmefra
                </span>
                <h3 className="font-serif font-bold text-2xl text-[#123F13] mb-2">Online</h3>
                <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                  Sessionen kan gennemføres fra et roligt sted med stabil forbindelse. Det kan være en hjælp, hvis transport tager meget energi, eller du bor langt væk.
                </p>
              </div>
              <p className="pt-4">
                <Link to="/online-sessioner" className="text-sm font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
                  Læs om online sessioner <ArrowRight size={14} />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 12: FAQ */}
      <section id="faq" className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
              Ofte stillede spørgsmål
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
              Spørgsmål om eksamensangst og præstationsangst
            </h2>
            <p className="text-[#3B352F]/70 text-lg">
              Her finder du svar på nogle af de spørgsmål, der ofte opstår, når nervøsiteten er blevet mere styrende end hjælpsom.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {faqData.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaqIndex === index}
                onToggle={() => toggleFaq(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Section 13: Faglig viden */}
      <section className="py-20 bg-[#123F13] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#FAF7F2] uppercase">
              Faglig viden
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white">
              Hvad ved man om angst, opmærksomhed og præstation?
            </h2>
            <p className="text-white/80 text-lg">
              Forskning peger blandt andet på, at angst kan konkurrere om opmærksomhedsressourcer og arbejdshukommelse. Det betyder ikke, at alle oplever det samme, eller at det at gå blank har én enkelt årsag.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 border border-white/20 p-6 rounded-2xl">
              <strong className="block text-lg font-serif mb-2 text-white">Opmærksomhed under angst</strong>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                I 2007 beskrev Eysenck m.fl. i Attentional Control Theory, hvordan angst kan påvirke styringen af opmærksomhed, især når trusler og bekymring får meget plads.
              </p>
              <a href="https://doi.org/10.1037/1528-3542.7.2.336" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#FAF7F2] hover:underline">
                Eysenck m.fl., 2007 &rarr;
              </a>
            </div>

            <div className="bg-white/10 border border-white/20 p-6 rounded-2xl">
              <strong className="block text-lg font-serif mb-2 text-white">Angst og arbejdshukommelse</strong>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                I 2016 samlede Moran forskning om sammenhængen mellem angst og arbejdshukommelse i en metaanalyse og forskningsoversigt.
              </p>
              <a href="https://doi.org/10.1037/bul0000051" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#FAF7F2] hover:underline">
                Moran, 2016 &rarr;
              </a>
            </div>

            <div className="bg-white/10 border border-white/20 p-6 rounded-2xl">
              <strong className="block text-lg font-serif mb-2 text-white">Behandling af testangst</strong>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                I 2003 samlede Ergene forskning om interventioner mod testangst. Metaanalysen peger på, at psykologiske indsatser kan reducere testangst, selv om metoder og resultater varierer.
              </p>
              <a href="https://scholar.google.com/scholar?q=%22Effective+interventions+on+test+anxiety+reduction%3A+A+meta-analysis%22+Ergene+2003" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#FAF7F2] hover:underline">
                Ergene, 2003 &rarr;
              </a>
            </div>
          </div>

          <p className="mt-8 text-xs text-white/70">
            Kilderne giver faglig baggrund om angst, opmærksomhed, arbejdshukommelse og testangst. De er ikke en garanti for, hvordan et bestemt behandlingsforløb vil virke for dig.
          </p>
        </div>
      </section>

      {/* Section 14: Læs også */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12 space-y-4">
            <span className="text-xs font-extrabold tracking-widest text-[#8C2F12] uppercase">
              Læs også
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#123F13]">
              Andre sider, der kan være relevante
            </h2>
            <p className="text-[#3B352F]/70 text-lg">
              Eksamensangst og præstationsangst kan overlappe med andre former for uro og selvfokus. Her er de mest nærliggende steder at læse videre.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/social-angst" className="border border-[#E8DDCE] rounded-2xl p-6 hover:border-[#123F13] hover:shadow-md transition-all flex flex-col justify-between h-32 group">
              <strong className="text-[#123F13] font-bold">Social angst</strong>
              <span className="text-[#8C2F12] font-bold group-hover:translate-x-1 transition-transform">&rarr;</span>
            </Link>
            <Link to="/tankemylder" className="border border-[#E8DDCE] rounded-2xl p-6 hover:border-[#123F13] hover:shadow-md transition-all flex flex-col justify-between h-32 group">
              <strong className="text-[#123F13] font-bold">Tankemylder</strong>
              <span className="text-[#8C2F12] font-bold group-hover:translate-x-1 transition-transform">&rarr;</span>
            </Link>
            <Link to="/soevnproblemer" className="border border-[#E8DDCE] rounded-2xl p-6 hover:border-[#123F13] hover:shadow-md transition-all flex flex-col justify-between h-32 group">
              <strong className="text-[#123F13] font-bold">Søvnproblemer</strong>
              <span className="text-[#8C2F12] font-bold group-hover:translate-x-1 transition-transform">&rarr;</span>
            </Link>
            <Link to="/metakognitiv-terapi" className="border border-[#E8DDCE] rounded-2xl p-6 hover:border-[#123F13] hover:shadow-md transition-all flex flex-col justify-between h-32 group">
              <strong className="text-[#123F13] font-bold">Metakognitiv terapi</strong>
              <span className="text-[#8C2F12] font-bold group-hover:translate-x-1 transition-transform">&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 15: Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#123F13] to-[#193f10] text-white relative overflow-hidden">
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-white/[0.03] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10 space-y-6 flex flex-col items-center">
          <div className="mb-2">
            <img 
              src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
              alt="Core Kvalitetsstempel" 
              className="w-24 h-24 rotate-[-6deg] drop-shadow-[0_10px_20px_rgba(0,0,0,0.3)] object-contain mx-auto"
            />
          </div>
          <span className="text-[#FAF7F2]/80 font-bold text-xs uppercase tracking-widest block">
            Et realistisk næste skridt
          </span>
          <h2 className="text-3xl md:text-5xl font-serif text-[#FAF7F2] font-semibold leading-tight max-w-3xl mx-auto">
            Du behøver ikke vente, til næste præstation føles helt uoverskuelig
          </h2>
          <p className="text-lg text-[#FAF7F2]/80 max-w-2xl mx-auto leading-relaxed">
            Du og jeg kan begynde med at skabe overblik over, hvad der sker før, under og efter situationen, og finde et realistisk næste skridt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 w-full">
            <Link 
              to="/bestil-tid" 
              className="inline-flex items-center justify-center bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md hover:-translate-y-0.5"
            >
              Book første behandlingssession
            </Link>
            <Link 
              to="/kontakt" 
              className="inline-flex items-center justify-center border-2 border-[#FAF7F2] text-[#FAF7F2] px-8 py-4 rounded-lg font-bold text-base hover:bg-white/10 transition-all"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Eksamensangst;
